import { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, openSigned } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { OwnershipChart } from "./share/OwnershipChart";
import { NewTransactionMenu } from "./share/NewTransactionMenu";
import { RegistryTabs } from "./share/RegistryTabs";
import { ValuationHistoryChart } from "./share/ValuationHistoryChart";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];
const PLEDGE_ROLES = ["Styreleder", "Daglig leder"];

export default function ShareRegistry() {
  const { companyId } = useParams();
  const { company } = useOutletContext();
  const { user } = useAuth();
  const [cap, setCap] = useState(null);
  const [classes, setClasses] = useState([]);
  const [txs, setTxs] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [uploads, setUploads] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/companies/${companyId}/cap-table`),
      api.get(`/companies/${companyId}/share-classes`),
      api.get(`/companies/${companyId}/transactions`),
      api.get(`/companies/${companyId}/settlements`),
      api.get(`/companies/${companyId}/agreements`),
      api.get(`/companies/${companyId}/share-uploads`),
    ]).then(([c, cl, t, st, ag, up]) => {
      setCap(c.data);
      setClasses(cl.data);
      setTxs(t.data);
      setSettlements(st.data);
      setAgreements(ag.data);
      setUploads(up.data);
    });
  }, [companyId]);

  useEffect(() => {
    setCap(null);
    load();
  }, [load]);

  const rows = cap?.rows || [];
  const canEdit = !!user?.is_admin || (company?.my_roles || []).some((r) => CHAIR_ROLES.includes(r));
  const canManagePledge = !!user?.is_admin || (company?.my_roles || []).some((r) => PLEDGE_ROLES.includes(r));

  const downloadCsv = async (endpoint, filename) => {
    try {
      const res = await api.get(`/companies/${companyId}/export/${endpoint}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Kunne ikke laste ned filen");
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Aksjeeierbok" description="Aksjeeierbok, aksjeklasser og aksjeoverdragelser med digital sluttseddel.">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="export-csv-btn">
              <FileSpreadsheet className="h-4 w-4" /> Eksporter (CSV)
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="export-shareholders" onClick={() => downloadCsv("shareholders.csv", "aksjeeierbok.csv")}>Aksjeeierbok</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-transactions" onClick={() => downloadCsv("transactions.csv", "transaksjoner.csv")}>Transaksjoner</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-settlements" onClick={() => downloadCsv("settlements.csv", "sluttsedler.csv")}>Sluttsedler</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" className="gap-2" data-testid="download-book-btn"
          onClick={() => openSigned(`/companies/${companyId}/shareholder-book/pdf-link`).catch(() => toast.error("Kunne ikke åpne aksjeeierboka"))}>
          <FileDown className="h-4 w-4" /> Aksjeeierbok (PDF)
        </Button>
        <NewTransactionMenu companyId={companyId} rows={rows} classes={classes} onChanged={load} canEdit={canEdit} />
      </PageHeader>

      <div className="mb-6">
        <ValuationHistoryChart companyId={companyId} refreshKey={txs.length} />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <OwnershipChart cap={cap} />
        <div className="lg:col-span-3">
          <RegistryTabs rows={rows} settlements={settlements} agreements={agreements} uploads={uploads} txs={txs} classes={classes} onChanged={load} companyId={companyId} canEdit={canEdit} isAdmin={!!user?.is_admin} canManagePledge={canManagePledge} />
        </div>
      </div>
    </div>
  );
}
